import {
  FC,
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
  memo,
} from "react";
import { Map, useMap } from "@vis.gl/react-google-maps";
import useLassoDrawing from "../../hooks/use-lasso-drawing";
import {
  AddressBarControl,
  TopMapControl,
  ZoomChange,
  ZoomMapControls,
} from "./map-controls";
import { Box, Typography } from "@mui/material";
import { MapComponentProps, MapMarker, LassoMode } from "./map.types";
import ClusteredMarkers from "./clusters/clustered-markers";
import DriverMarkers from "./driver-markers";
import { MAP_STYLES } from "../../shared/constants/map-styles";
import MapLoading from "../../shared/components/map/MapLoading";
import { fitMapToMarkers } from "../../utils/map-utils";

type MapContentProps = Omit<
  MapComponentProps,
  | "mapWidth"
  | "mapHeight"
  | "isLoading"
  | "hasLiveStateChanges"
  | "hasPlannedChanges"
>;

const MapContent: FC<MapContentProps> = ({
  markers,
  driverMarkers,
  mapZoom,
  isSearchLoading,
  searchedAddresses,
  sequencingUnassigned,
  lassoMode,
  setQuery,
  setSelectedAddress,
  setSidebar,
  mapCentering,
  drawing = false,
  lastSequence,
  query,
  mapFilters,
  sequencing,
  selectedAddresses,
  showTopMapControl = true,
  onMapZoomChange,
  setMapCentering,
  onDrawingToggle,
  setAddressSequence,
  onSelectedMarkersChange,
  onMarkerClick,
  onSearchResultSelect,
  onResetSelection,
  existingSelection,
  onAddCustomer,
}) => {
  const [internalLassoMode, setInternalLassoMode] = useState<LassoMode>(
    LassoMode.REPLACE,
  );
  const currentLassoMode = lassoMode || internalLassoMode;
  const mapInstance = useMap();
  const SINGLE_MARKER_ZOOM = 17;
  const CENTER_EPSILON = 0.000001;
  const initialFitCompletedRef = useRef(false);

  const handleDrawingToggle = (
    isDrawing: boolean,
    mode: LassoMode = LassoMode.REPLACE,
  ) => {
    setInternalLassoMode(mode);
    onDrawingToggle?.(isDrawing, mode);
  };

  useLassoDrawing({
    markers,
    drawing,
    onSelectedMarkersChange,
    onDrawingComplete: () => {
      setInternalLassoMode(LassoMode.REPLACE);
      onDrawingToggle?.(false);
    },
    mode: currentLassoMode,
    currentSelection: selectedAddresses || [],
    onResetSelection,
  });

  const toggleZooming = (zoom: ZoomChange) => {
    if (!mapInstance) return;

    const currentZoom = mapInstance.getZoom() ?? mapZoom;
    let newZoom: number;

    if (zoom === ZoomChange.Increase) {
      newZoom = currentZoom + 1;
    } else if (zoom === ZoomChange.Decrease && currentZoom > 1) {
      newZoom = currentZoom - 1;
    } else {
      return;
    }

    // Use Google Maps' smooth zoom animation
    mapInstance.setZoom(newZoom);
    // Update state for persistence
    onMapZoomChange(newZoom);
  };

  const handleMarkerClick = useCallback(
    (event: google.maps.MapMouseEvent, marker: MapMarker) => {
      // If a custom onMarkerClick handler is provided, use it
      if (onMarkerClick) {
        const isShiftPressed =
          event.domEvent && (event.domEvent as MouseEvent).shiftKey;
        onMarkerClick(marker, isShiftPressed, event);
        return;
      }

      // Default behavior for internal map logic
      setMapCentering?.(undefined);
      if (sequencing) {
        setAddressSequence?.(
          marker.id,
          lastSequence && lastSequence === 0 ? 1 : lastSequence,
          sequencingUnassigned,
        );
      } else {
        const isShiftPressed =
          event.domEvent && (event.domEvent as MouseEvent).shiftKey;
        setSelectedAddress?.([marker], isShiftPressed || existingSelection);
      }
    },
    [
      onMarkerClick,
      sequencing,
      lastSequence,
      setAddressSequence,
      setSelectedAddress,
      setMapCentering,
      existingSelection,
    ],
  );

  useEffect(() => {
    setSidebar?.((selectedAddresses && selectedAddresses.length > 0) || false);
  }, [selectedAddresses]);

  // Effect for initial render - centers map on all markers
  useEffect(() => {
    if (!mapInstance || initialFitCompletedRef.current) return;

    if (markers && markers.length > 0) {
      // Initial load: don't enforce min zoom, let fitBounds show all addresses naturally
      fitMapToMarkers(mapInstance, markers, onMapZoomChange, false);
      initialFitCompletedRef.current = true;
    }
  }, [mapInstance, markers, onMapZoomChange]);

  // Effect for address selection - centers map on specific addresses
  // For single address: zoom out a bit to show surrounding context
  // For multiple addresses: fit to bounds
  useEffect(() => {
    if (!mapInstance) return;

    if (mapCentering && mapCentering.length > 0) {
      if (mapCentering.length === 1) {
        // Single address: center on it and set a reasonable zoom level
        const marker = mapCentering[0];
        if (marker.latitude && marker.longitude) {
          const currentCenter = mapInstance.getCenter()?.toJSON();
          const nextCenter = {
            lat: marker.latitude,
            lng: marker.longitude,
          };
          const shouldUpdateCenter =
            !currentCenter ||
            Math.abs(currentCenter.lat - nextCenter.lat) > CENTER_EPSILON ||
            Math.abs(currentCenter.lng - nextCenter.lng) > CENTER_EPSILON;
          const currentZoom = mapInstance.getZoom();
          const shouldUpdateZoom = currentZoom !== SINGLE_MARKER_ZOOM;

          if (shouldUpdateCenter) {
            mapInstance.setCenter(nextCenter);
          }

          if (shouldUpdateZoom) {
            mapInstance.setZoom(SINGLE_MARKER_ZOOM);
            onMapZoomChange(SINGLE_MARKER_ZOOM);
          }
        }
      } else {
        // Multiple addresses (region/route selection): enforce min zoom to prevent being too zoomed out
        fitMapToMarkers(mapInstance, mapCentering, onMapZoomChange, true);
      }
    }
  }, [mapInstance, mapCentering, onMapZoomChange]);

  const memoizedMarkers = useMemo(() => markers, [markers]);

  return (
    <Map
      mapId="advancedMarkerId"
      defaultCenter={{ lat: 39.3418, lng: -120.2144 }}
      defaultZoom={mapZoom}
      minZoom={4}
      gestureHandling="cooperative"
      disableDefaultUI={true}
      disableDoubleClickZoom={true}
      styles={MAP_STYLES}
      reuseMaps
    >
      {!!memoizedMarkers.length && (
        <ClusteredMarkers
          markers={memoizedMarkers}
          onMarkerClick={handleMarkerClick}
        />
      )}
      <DriverMarkers markers={driverMarkers} />
      {showTopMapControl && (
        <TopMapControl
          onDrawingChange={handleDrawingToggle}
          drawing={drawing}
          mapFilters={mapFilters}
          selectedAddresses={selectedAddresses}
          onResetSelection={onResetSelection}
        />
      )}
      <AddressBarControl
        markers={markers}
        searchOptions={searchedAddresses || []}
        isLoading={isSearchLoading}
        query={query || ""}
        setQuery={setQuery}
        setSelectedAddress={setSelectedAddress || (() => void 0)}
        setMapCentering={setMapCentering || (() => void 0)}
        onAddCustomer={onAddCustomer}
        onSearchResultSelect={onSearchResultSelect}
      />
      <ZoomMapControls onZoomChange={(zoom) => toggleZooming(zoom)} />
    </Map>
  );
};

const MemoizedMapContent = memo(MapContent);

const MapComponent: FC<MapComponentProps> = ({
  mapWidth,
  mapHeight,
  isLoading,
  hasLiveStateChanges,
  hasPlannedChanges,
  ...mapContentProps
}) => {
  return (
    <Box style={{ position: "relative" }}>
      <Box
        data-testid="map-container"
        style={{
          width: mapWidth,
          height: mapHeight,
          position: "relative",
          borderRadius: "12px",
          overflow: "hidden",
          border:
            hasLiveStateChanges || hasPlannedChanges ? "2px solid red" : "none",
        }}
      >
        <MemoizedMapContent {...mapContentProps} />

        {/* Loading overlay - completely hides map until ready */}
        {isLoading && <MapLoading hasLiveStateChanges={hasLiveStateChanges} />}
      </Box>

      {(hasLiveStateChanges || hasPlannedChanges) && (
        <Typography
          color="#F43959"
          fontWeight={400}
          variant="bodySmall"
          sx={{
            position: "absolute",
            top: "100%",
            right: "0",
            marginLeft: "auto",
          }}
        >
          {hasPlannedChanges ? "EST Not Updated" : "Route not Updated"}
        </Typography>
      )}
    </Box>
  );
};

export default memo(MapComponent);
